import { useQuery } from '@tanstack/react-query'
import { useServerApiVersion } from 'features/identificationVisits/hooks/useServerApiVersion'
import { ServerApiVersion } from 'features/identificationVisits/types'

import { extractData, useBuildRequest } from './base'

export interface UseEventParams {
  subscriptionId: string
  requestId: string
  enabled?: boolean
}

export function eventQueryOptionsV3(
  buildRequest: ReturnType<typeof useBuildRequest>,
  subscriptionId: string,
  requestId: string,
  enabled = true
) {
  return {
    queryKey: ['event', subscriptionId, requestId] as const,
    queryFn: () =>
      extractData(
        buildRequest('eventGet', {
          params: {
            subscriptionId,
            requestId,
          },
        })
      ),
    enabled: enabled && subscriptionId != null && requestId != null,
    staleTime: Infinity,
  }
}

export function eventQueryOptionsV4(
  buildRequest: ReturnType<typeof useBuildRequest>,
  subscriptionId: string,
  requestId: string,
  enabled = true
) {
  return {
    queryKey: ['eventV4', subscriptionId, requestId] as const,
    queryFn: () =>
      extractData(
        buildRequest('eventGetV4', {
          params: { subscriptionId, requestId },
        })
      ),
    enabled: enabled && subscriptionId != null && requestId != null,
    staleTime: Infinity,
  }
}

export function useEventQueryV3({ subscriptionId, requestId, enabled = true }: UseEventParams) {
  const buildRequest = useBuildRequest()
  return useQuery(eventQueryOptionsV3(buildRequest, subscriptionId, requestId, enabled))
}

export function useEventQueryV4({ subscriptionId, requestId, enabled = true }: UseEventParams) {
  const buildRequest = useBuildRequest()
  return useQuery(eventQueryOptionsV4(buildRequest, subscriptionId, requestId, enabled))
}

export function useEventJson({ subscriptionId, requestId, enabled = true }: UseEventParams) {
  const { serverApiVersion } = useServerApiVersion()
  const isV3 = serverApiVersion === ServerApiVersion.V3

  const v3 = useEventQueryV3({ subscriptionId, requestId, enabled: enabled && isV3 })
  const v4 = useEventQueryV4({ subscriptionId, requestId, enabled: enabled && !isV3 })

  return isV3 ? v3 : v4
}
